import Phaser from 'phaser';
import { PALETTE } from '../config';

export interface PowerMeterOptions {
  width?: number;
  height?: number;
  /** Optional target band [min,max] in 0..1, drawn as a highlighted "sweet spot". */
  target?: [number, number];
  fillColor?: number;
  hotColor?: number;
}

/**
 * Horizontal meter with a fill, an optional target band (the cadence sweet spot), and
 * a needle marker. Used for the sprint speed/cadence read-out. Fixed to the camera.
 */
export class PowerMeter extends Phaser.GameObjects.Container {
  private g: Phaser.GameObjects.Graphics;
  private readonly boxW: number;
  private readonly boxH: number;
  private readonly opts: Required<Pick<PowerMeterOptions, 'fillColor' | 'hotColor'>> & PowerMeterOptions;
  private fill = 0;
  private needle = -1;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: PowerMeterOptions = {}) {
    super(scene, x, y);
    this.boxW = opts.width ?? 360;
    this.boxH = opts.height ?? 26;
    this.opts = {
      fillColor: opts.fillColor ?? PALETTE.power,
      hotColor: opts.hotColor ?? PALETTE.powerHot,
      ...opts,
    };
    this.g = scene.add.graphics();
    this.add(this.g);
    this.setScrollFactor(0);
    scene.add.existing(this);
    this.redraw();
  }

  /** value: 0..1 fill. needle: 0..1 position or <0 to hide. */
  set(value: number, needle = -1): void {
    this.fill = Phaser.Math.Clamp(value, 0, 1);
    this.needle = needle;
    this.redraw();
  }

  private redraw(): void {
    const { boxW: w, boxH: h } = this;
    this.g.clear();
    // Track.
    this.g.fillStyle(PALETTE.ink, 0.55).fillRoundedRect(-w / 2 - 4, -h / 2 - 4, w + 8, h + 8, 10);
    this.g.fillStyle(PALETTE.trackLine, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    // Target band.
    if (this.opts.target) {
      const [a, b] = this.opts.target;
      this.g.fillStyle(PALETTE.good, 0.32).fillRect(-w / 2 + a * w, -h / 2, (b - a) * w, h);
    }
    // Fill, turning hot near the top.
    const color = this.fill > 0.82 ? this.opts.hotColor : this.opts.fillColor;
    this.g.fillStyle(color, 1).fillRoundedRect(-w / 2, -h / 2, this.fill * w, h, 8);
    // Needle.
    if (this.needle >= 0) {
      const nx = -w / 2 + this.needle * w;
      this.g.fillStyle(PALETTE.white, 1).fillRect(nx - 2, -h / 2 - 6, 4, h + 12);
    }
  }
}
